import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import FloatingPanel from "../FloatingPanel";
import TeamScoresSubheadingRow from "./TeamScoresSubheadingRow";
import { formatScoreValue, toScoreValue } from "../../utils/scoreState";
import { getScorePillProps } from "../../utils/scoringScale";

// A subheading is worth listing once anyone has scored it, or once it has a
// median or team score on the scale.
const hasAnyScore = (subheading, scale) =>
  (subheading?.collaboratorScores?.length || 0) > 0 ||
  toScoreValue(subheading?.teamScore, scale) !== null ||
  toScoreValue(subheading?.median, scale) !== null;

const TeamScoresOverlay = ({
  isOpen,
  onClose,
  title = "Team Scores",
  startLabel,
  endLabel,
  headings = [],
  isLoading,
  error,
}) => {
  const [expandedAccordions, setExpandedAccordions] = useState([]);
  const [openCommentDropdownId, setOpenCommentDropdownId] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setExpandedAccordions([]);
      setOpenCommentDropdownId(null);
    }
  }, [isOpen]);

  const filteredHeadings = useMemo(() => {
    if (!Array.isArray(headings)) return [];
    return headings.filter((heading) =>
      heading?.subheadings?.some((subheading) =>
        hasAnyScore(subheading, heading?.scoringScale)
      )
    );
  }, [headings]);

  // N/A is displayed exactly like an unscored item: blank. So the only thing
  // ever rendered for a score is a value the heading's scale defines.
  const normalizeScoreValue = (score, scale) =>
    formatScoreValue(score, "", scale);

  // Takes an already-normalized value, so anything that is not a value on the
  // scale (i.e. blank) falls through to the neutral pill.
  const getScorePillStyle = (scoreValue, scale, naClass = "head_light") =>
    getScorePillProps(scale, scoreValue, naClass);

  const formatContributorName = (contributor) => {
    const lastName = contributor?.lastname || contributor?.lastName || "";
    const firstName = contributor?.firstname || contributor?.firstName || "";
    const name = [lastName, firstName].filter(Boolean).join(", ") || "Unknown";
    const position =
      contributor?.position?.abbreviation ||
      contributor?.position?.name ||
      "";
    return position ? `${name} (${position})` : name;
  };

  const getSubheadingComments = (subheading) => {
    if (!subheading?.collaboratorScores) return [];
    return subheading.collaboratorScores
      .filter(
        (contributor) =>
          contributor?.comment &&
          String(contributor.comment).trim().length > 0
      )
      .map((contributor, index) => ({
        id: contributor?.id || `${subheading.id || "sub"}-${index}`,
        name: formatContributorName(contributor),
        comment: contributor.comment,
      }));
  };

  return (
    <FloatingPanel
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={
        startLabel && endLabel
          ? `Time period: ${startLabel} to ${endLabel}`
          : null
      }
      width={606}
      height={468}
      bodyProps={{ onClick: () => setOpenCommentDropdownId(null) }}
    >
      {isLoading && (
        <div className="text-center py-4">
          <p className="mb-0">Loading team scores...</p>
        </div>
      )}
      {error && (
        <div className="text-center py-4 text-danger">
          <p className="mb-0">
            Error loading team scores. Please try again.
          </p>
        </div>
      )}
      {!isLoading && !error && filteredHeadings.length === 0 && (
        <div className="text-center py-4 text-muted">
          <p className="mb-0">No team scores available for this period.</p>
        </div>
      )}
      {!isLoading && !error && filteredHeadings.length > 0 && (
        <Accordion
          allowZeroExpanded
          allowMultipleExpanded
          className="mb-0"
          preExpanded={expandedAccordions}
          onChange={setExpandedAccordions}
        >
          {filteredHeadings.map((heading, headingIndex) => {
            const scale = heading?.scoringScale;
            const subheadings =
              heading?.subheadings?.filter((subheading) =>
                hasAnyScore(subheading, scale)
              ) || [];

            return (
              <div key={heading.id || headingIndex}>
                <div className="chevron" />
                <AccordionItem
                  className="mb-2"
                  uuid={`team-score-${heading.id || headingIndex}`}
                >
                  <AccordionItemHeading className="accordion__primary">
                    <AccordionItemButton
                      style={{
                        height: 45,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <small>{heading.headingName}</small>
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <div className="d-flex justify-content-between text-center text-white head_light mx-0 h-27 small-text">
                      <div className="flex-grow-1" style={{ width: 280 }}>
                        <small className="d-flex justify-content-center align-items-center h-100">
                          Functional Issue
                        </small>
                      </div>
                      <div
                        className="border-left-1 d-flex align-items-center justify-content-center flex-grow-1"
                        style={{ minWidth: 200 }}
                      >
                        <small className="d-flex justify-content-center align-items-center h-100">
                          Comments
                        </small>
                      </div>
                      <div
                        className="border-left-1 d-flex align-items-center justify-content-center"
                        style={{ width: 90 }}
                      >
                        <small className="d-flex justify-content-center align-items-center h-100">
                          Median
                        </small>
                      </div>
                      <div
                        className="border-left-1 d-flex align-items-center justify-content-center"
                        style={{ width: 90 }}
                      >
                        <small className="d-flex justify-content-center align-items-center h-100">
                          Team Score
                        </small>
                      </div>
                    </div>

                    {subheadings.length === 0 && (
                      <div className="py-3 text-center text-muted">
                        <small>No team scores for this heading.</small>
                      </div>
                    )}

                    {subheadings.map((subheading, subIndex) => (
                      <TeamScoresSubheadingRow
                        key={subheading.id || subIndex}
                        subheading={subheading}
                        subIndex={subIndex}
                        openCommentDropdownId={openCommentDropdownId}
                        setOpenCommentDropdownId={setOpenCommentDropdownId}
                        scale={scale}
                        normalizeScoreValue={normalizeScoreValue}
                        getScorePillStyle={getScorePillStyle}
                        getSubheadingComments={getSubheadingComments}
                      />
                    ))}
                  </AccordionItemPanel>
                </AccordionItem>
              </div>
            );
          })}
        </Accordion>
      )}
    </FloatingPanel>
  );
};

export default TeamScoresOverlay;
